import { palette, spaces } from '@kimuno/theme';
import { Box, Chip, Typography } from '@mui/material';
import type React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface ChipsSelectorFieldProps {
	name: string;
	control: any;
	options: string[];
	max?: number;
	isEditMode?: boolean;
	defaultValue?: string[];
	error?: string;
}

const ChipsSelectorField: React.FC<ChipsSelectorFieldProps & { error?: string }> = ({
	name,
	control,
	options,
	max = Number.POSITIVE_INFINITY,
	isEditMode = false,
	defaultValue = [],
	error,
}) => {
	const { t } = useTranslation();

	const ChipsSelectorWrapper = ({ value = [], onChange }: any) => {
		const selected = value;
		const isMaxReached = selected.length >= max;
		const availableOptions = options.filter((opt) => !selected.includes(opt));

		const handleAdd = (item: string) => {
			if (!isMaxReached) {
				onChange([...selected, item]);
			}
		};

		const handleDelete = (item: string) => {
			onChange(selected.filter((i: string) => i !== item));
		};

		const showError = Boolean(error) && isEditMode;

		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md }}>
				{selected.length === 0 ? (
					<Typography
						variant={showError ? 'caption' : undefined}
						color={showError ? palette.error.main : palette.text.disabled}
						sx={
							showError
								? undefined
								: {
										fontFamily: 'Poppins',
										fontStyle: 'normal',
										fontWeight: 400,
										lineHeight: '24px',
										letterSpacing: '0.15px',
									}
						}
					>
						{t(showError ? (error as string) : 'Not set')}
					</Typography>
				) : (
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: spaces.xs,
						}}
					>
						{selected.map((item: string) => (
							<Chip
								data-testid={`chip-${item}`}
								key={item}
								label={item}
								onDelete={isEditMode ? () => handleDelete(item) : undefined}
								sx={{
									background: 'transparent',
									border: `1px solid ${palette.components.chip.defaultEnabled}`,
								}}
							/>
						))}
					</Box>
				)}
				{isEditMode && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: spaces.xs,
						}}
					>
						<Typography variant="caption" color={palette.text.disabled}>
							{t('Available options:')}
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: spaces.xs,
							}}
						>
							{availableOptions.map((option: string) => (
								<Chip
									key={option}
									label={option}
									onClick={() => handleAdd(option)}
									disabled={isMaxReached}
									sx={{
										background: 'transparent',
										border: `1px solid ${palette.components.chip.defaultEnabled}`,
									}}
								/>
							))}
						</Box>
					</Box>
				)}
			</Box>
		);
	};

	return (
		<Controller
			name={name}
			control={control}
			defaultValue={defaultValue}
			render={({ field }) => <ChipsSelectorWrapper value={field.value} onChange={field.onChange} />}
		/>
	);
};

export default ChipsSelectorField;
